// Ctrl+F search modal — the sidebar's region search, surfaced as a focused
// overlay (same shell as the command palette). Matches by region name,
// country, ID or geographical area; Enter/click centers the map. Open state
// is owned by the caller so there's a single source of truth.
import { useEffect, useMemo, useRef, useState } from 'react'
import Icon from './Icons'
import { getCountryFlag } from '../utils/flagCache'
import { searchRegions } from '../utils/regionSearch'
import '../styles/CommandPalette.css'

const SearchModal = ({ open, onClose, regionsData, onCenterMap, organizationsCatalog, weatherData }) => {
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const [flags, setFlags] = useState({})
  const inputRef = useRef(null)

  useEffect(() => {
    if (open) {
      setQuery('')
      setHighlight(0)
      const t = setTimeout(() => inputRef.current?.focus(), 0)
      return () => clearTimeout(t)
    }
  }, [open])

  const results = useMemo(
    () => searchRegions(regionsData, query, { organizationsCatalog, weatherData, limit: 20 }),
    [query, regionsData, organizationsCatalog, weatherData]
  )

  useEffect(() => setHighlight(0), [query])

  useEffect(() => {
    results.forEach((r) => {
      if (r.country_id && !(r.country_id in flags)) {
        getCountryFlag(r.country_id, (url) => {
          setFlags((prev) => ({ ...prev, [r.country_id]: url }))
        })
      }
    })
  }, [results]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null

  const select = (r) => {
    if (!r) return
    onCenterMap(r)
    onClose()
  }

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => Math.min(h + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      select(results[highlight])
    }
  }

  return (
    <div className="command-palette-overlay" onClick={onClose}>
      <div className="command-palette" onClick={(e) => e.stopPropagation()}>
        <div className="command-palette-input-row">
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            type="text"
            placeholder="Région, pays, zone, climat, météo, organisation, population (ex : >1M)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd>Esc</kbd>
        </div>
        <div className="command-palette-results">
          {query.trim() && results.length === 0 && (
            <div className="command-palette-empty">Aucun résultat</div>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.region_id}-${i}`}
              className={`command-palette-item search-result-row ${i === highlight ? 'highlight' : ''}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => select(r)}
            >
              <span className="search-result-badge">{r.matchType}</span>
              <span className="search-result-main">
                #{r.region_id} — {r.name}
                {r.country_name && (
                  <span className="search-result-country">
                    {flags[r.country_id] && (
                      <img src={flags[r.country_id]} alt="" className="country-flag-icon-sm" />
                    )}
                    {r.country_name}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default SearchModal
